import L, { type LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ArrowDown,
  ArrowUp,
  Download,
  ExternalLink,
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Save,
  WandSparkles,
  X,
  createIcons
} from "lucide";
import { buildGoogleMapsDirectionsUrl, formatDistanceKm, optimizeRouteOrder, totalRouteDistanceKm } from "./lib/geo";
import { hasMapTilerKey, normalizeMapTilerKey, redactMapTilerKey } from "./lib/mapConfig";
import { seedPlaces } from "./lib/placeData";
import { createTemporaryPlace, filterPlaces, getFilterOptions, mergePlaces, parseCoordinateInput } from "./lib/places";
import type { Place, PlaceFilters, PlacePriority, PlaceType, CoordinateConfidence } from "./lib/types";
import "./styles.css";

const LOCAL_PLACES_KEY = "mapitup.localPlaces.v1";
const ROUTE_IDS_KEY = "mapitup.routeIds.v1";
const BASE_ID_KEY = "mapitup.baseId.v1";
const LABELS_KEY = "mapitup.showChineseLabels.v1";
const MAPTILER_KEY_STORAGE_KEY = "mapitup.mapTilerKey.v1";
const SIDEBAR_COLLAPSED_KEY = "mapitup.sidebarCollapsed.v1";
const LABEL_MIN_ZOOM = 10;
const BUNDLED_MAPTILER_KEY = normalizeMapTilerKey(import.meta.env.VITE_MAPTILER_KEY);

const TYPE_META: Record<PlaceType, { label: string; glyph: string }> = {
  hotel: { label: "酒店", glyph: "H" },
  station: { label: "车站", glyph: "S" },
  attraction: { label: "景点", glyph: "P" },
  restaurant: { label: "餐饮", glyph: "R" },
  temporary: { label: "临时", glyph: "T" }
};

const LUCIDE_ICONS = {
  ArrowDown,
  ArrowUp,
  Download,
  ExternalLink,
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Save,
  WandSparkles,
  X
};

const PRIORITY_LABELS: Record<PlacePriority, string> = {
  base: "基点",
  must: "必去",
  optional: "可选",
  maybe: "待定"
};

const CONFIDENCE_LABELS: Record<CoordinateConfidence, string> = {
  exact: "已校准",
  approximate: "近似",
  unverified: "待确认"
};

interface AppState {
  filters: PlaceFilters;
  query: string;
  localPlaces: Place[];
  routeIds: string[];
  baseId: string;
  selectedPlaceId: string | null;
  draftCoordinate: { lat: number; lng: number } | null;
  showChineseLabels: boolean;
  mapTilerKey: string;
  sidebarCollapsed: boolean;
}

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("App root not found");
}

app.innerHTML = `
  <div class="app-shell">
    <aside class="sidebar" aria-label="MapItUp controls">
      <div id="sidebar-content"></div>
    </aside>
    <main class="map-stage">
      <button class="floating-panel-button" type="button" data-action="expand-sidebar" title="展开控制台" aria-label="展开控制台">
        <i data-lucide="panel-left-open"></i>
      </button>
      <div id="map" aria-label="Japan trip map"></div>
    </main>
  </div>
`;

const map = L.map("map", {
  zoomControl: false
}).setView([35.69, 139.76], 12);

L.control.zoom({ position: "bottomright" }).addTo(map);

const cartoVoyagerLayer = L.tileLayer(
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
  {
    maxZoom: 20,
    maxNativeZoom: 19,
    subdomains: "abcd",
    updateWhenIdle: true,
    keepBuffer: 4,
    crossOrigin: true,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
).addTo(map);

const cartoLightLayer = L.tileLayer(
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
  {
    maxZoom: 20,
    maxNativeZoom: 19,
    subdomains: "abcd",
    updateWhenIdle: true,
    keepBuffer: 4,
    crossOrigin: true,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
  }
);

const osmStandardLayer = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
  maxZoom: 19,
  maxNativeZoom: 19,
  updateWhenIdle: true,
  keepBuffer: 4,
  crossOrigin: true,
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
});

const initialMapTilerKey = loadMapTilerKey();
let mapTilerChineseLayer: L.Layer | null = null;
const fallbackBaseLayers = [cartoVoyagerLayer, cartoLightLayer, osmStandardLayer];
const baseLayerEntries: Record<string, L.Layer> = {
  "CARTO Voyager": cartoVoyagerLayer,
  "CARTO Light": cartoLightLayer,
  "OSM Standard": osmStandardLayer
};

const baseLayerControl = L.control.layers(baseLayerEntries, undefined, {
  position: "topright"
});

cartoVoyagerLayer.addTo(map);
baseLayerControl.addTo(map);

const markerLayer = L.layerGroup().addTo(map);
const routeLayer = L.layerGroup().addTo(map);
let draftMarker: L.Marker | null = null;
let hasFitInitialBounds = false;

const state: AppState = {
  filters: {
    city: "all",
    types: new Set<PlaceType>(),
    priority: "all",
    confidence: "all"
  },
  query: "",
  localPlaces: loadLocalPlaces(),
  routeIds: loadRouteIds(),
  baseId: localStorage.getItem(BASE_ID_KEY) ?? "hotel-monterey-hanzomon",
  selectedPlaceId: null,
  draftCoordinate: null,
  showChineseLabels: localStorage.getItem(LABELS_KEY) !== "false",
  mapTilerKey: initialMapTilerKey,
  sidebarCollapsed: localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
};

if (hasMapTilerKey(initialMapTilerKey)) {
  void installMapTilerChineseLayer(initialMapTilerKey);
}

app.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const floatingAction = target.closest<HTMLElement>(".floating-panel-button[data-action]");
  if (floatingAction) {
    handleAction(floatingAction);
  }
});

map.on("zoomend", () => {
  renderMarkers();
});

map.on("click", (event) => {
  state.draftCoordinate = {
    lat: roundCoordinate(event.latlng.lat),
    lng: roundCoordinate(event.latlng.lng)
  };
  syncDraftCoordinateInput();
  renderDraftMarker();
});

render();

function render(): void {
  renderShellState();
  renderSidebar();
  renderMarkers();
  renderRoute();
  createIcons({ icons: LUCIDE_ICONS });
}

function renderShellState(): void {
  const shell = document.querySelector<HTMLDivElement>(".app-shell");
  shell?.classList.toggle("is-sidebar-collapsed", state.sidebarCollapsed);
}

function renderSidebar(): void {
  const sidebar = document.querySelector<HTMLDivElement>("#sidebar-content");
  if (!sidebar) {
    return;
  }

  const places = allPlaces();
  const visiblePlaces = getVisiblePlaces();
  const options = getFilterOptions(places);
  const baseOptions = places.filter((place) => place.type === "hotel" || place.type === "station");
  const routePlaces = getRoutePlaces();
  const base = getBasePlace();
  const routePath = base ? [base, ...routePlaces] : routePlaces;
  const selectedPlace = places.find((place) => place.id === state.selectedPlaceId);

  sidebar.innerHTML = `
    <header class="sidebar-header">
      <div>
        <h1>MapItUp</h1>
        <span>Japan Route Desk</span>
      </div>
      <div class="header-actions">
        <button class="icon-button" type="button" data-action="collapse-sidebar" title="收起控制台" aria-label="收起控制台">
          <i data-lucide="panel-left-close"></i>
        </button>
        <button class="icon-button" type="button" data-action="export-json" title="导出 JSON" aria-label="导出 JSON">
          <i data-lucide="download"></i>
        </button>
      </div>
    </header>

    <div class="summary-strip" aria-label="summary">
      <div><strong>${places.length}</strong><span>地点</span></div>
      <div><strong>${visiblePlaces.length}</strong><span>可见</span></div>
      <div><strong>${routePlaces.length}</strong><span>路线</span></div>
      <div><strong>${formatDistanceKm(totalRouteDistanceKm(routePath))}</strong><span>直线</span></div>
    </div>

    <section class="tool-section" aria-labelledby="filters-title">
      <div class="section-title">
        <h2 id="filters-title">筛选</h2>
        <button class="text-button" type="button" data-action="reset-filters">重置</button>
      </div>
      <div class="segmented-control" role="group" aria-label="城市">
        ${cityButton("all", "全部")}
        ${options.cities.map((city) => cityButton(city, city)).join("")}
      </div>
      <label class="field">
        <span>搜索</span>
        <input data-field="query" type="search" value="${escapeHtml(state.query)}" placeholder="名称 / 区域" />
      </label>
      <div class="toggle-grid" aria-label="类型">
        ${options.types.map((type) => typeToggle(type)).join("")}
      </div>
      <div class="field-grid">
        <label class="field">
          <span>优先级</span>
          <select data-field="priority">
            ${option("all", "全部", state.filters.priority)}
            ${Object.entries(PRIORITY_LABELS).map(([value, label]) => option(value, label, state.filters.priority)).join("")}
          </select>
        </label>
        <label class="field">
          <span>坐标</span>
          <select data-field="confidence">
            ${option("all", "全部", state.filters.confidence)}
            ${Object.entries(CONFIDENCE_LABELS).map(([value, label]) => option(value, label, state.filters.confidence)).join("")}
          </select>
        </label>
      </div>
      <label class="check-row">
        <input data-field="show-labels" type="checkbox" ${state.showChineseLabels ? "checked" : ""} />
        <span>中文点名</span>
      </label>
    </section>

    <section class="tool-section" aria-labelledby="basemap-title">
      <div class="section-title">
        <h2 id="basemap-title">中文底图</h2>
        <span class="status-chip ${hasMapTilerKey(state.mapTilerKey) ? "status-chip--ready" : ""}">
          ${hasMapTilerKey(state.mapTilerKey) ? "已启用" : "未配置"}
        </span>
      </div>
      <form id="maptiler-key-form" class="key-form">
        <label class="field">
          <span>MapTiler Key</span>
          <input name="mapTilerKey" type="password" autocomplete="off" value="${escapeHtml(state.mapTilerKey)}" />
        </label>
        <div class="key-actions">
          <button class="secondary-button" type="submit">
            <i data-lucide="save"></i>
            <span>保存</span>
          </button>
          <button class="secondary-button" type="button" data-action="clear-maptiler-key" ${hasMapTilerKey(state.mapTilerKey) ? "" : "disabled"}>
            <i data-lucide="x"></i>
            <span>清除</span>
          </button>
        </div>
      </form>
      <div class="key-status">
        ${hasMapTilerKey(state.mapTilerKey) ? `Key ${escapeHtml(redactMapTilerKey(state.mapTilerKey))}` : "CARTO"}
      </div>
    </section>

    <section class="tool-section" aria-labelledby="add-title">
      <div class="section-title">
        <h2 id="add-title">新地点</h2>
      </div>
      <form id="add-place-form" class="add-form">
        <label class="field">
          <span>名称</span>
          <input name="name" autocomplete="off" required />
        </label>
        <div class="field-grid">
          <label class="field">
            <span>城市</span>
            <input name="city" list="city-options" value="${state.filters.city === "all" ? "Tokyo" : escapeHtml(state.filters.city)}" required />
            <datalist id="city-options">
              ${options.cities.map((city) => `<option value="${escapeHtml(city)}"></option>`).join("")}
            </datalist>
          </label>
          <label class="field">
            <span>类型</span>
            <select name="type">
              ${Object.entries(TYPE_META).map(([value, meta]) => option(value, meta.label, "temporary")).join("")}
            </select>
          </label>
        </div>
        <label class="field">
          <span>坐标 / URL</span>
          <input name="coordinates" data-field="draft-coordinate" autocomplete="off" value="${state.draftCoordinate ? `${state.draftCoordinate.lat}, ${state.draftCoordinate.lng}` : ""}" required />
        </label>
        <label class="field">
          <span>区域</span>
          <input name="addressHint" autocomplete="off" />
        </label>
        <label class="field">
          <span>备注</span>
          <textarea name="notes" rows="2"></textarea>
        </label>
        <button class="primary-button" type="submit">
          <i data-lucide="plus"></i>
          <span>加入地图</span>
        </button>
      </form>
    </section>

    <section class="tool-section" aria-labelledby="route-title">
      <div class="section-title">
        <h2 id="route-title">路线</h2>
        <button class="text-button" type="button" data-action="clear-route">清空</button>
      </div>
      <label class="field">
        <span>起点</span>
        <select data-field="base">
          ${baseOptions.map((place) => option(place.id, place.name, state.baseId)).join("")}
        </select>
      </label>
      <div class="route-actions">
        <button class="secondary-button" type="button" data-action="optimize-route" ${routePlaces.length < 2 ? "disabled" : ""}>
          <i data-lucide="wand-sparkles"></i>
          <span>粗排</span>
        </button>
        <button class="secondary-button" type="button" data-action="open-google" ${routePath.length < 2 ? "disabled" : ""}>
          <i data-lucide="external-link"></i>
          <span>Google</span>
        </button>
      </div>
      <ol class="route-list">
        ${routePlaces.length === 0 ? `<li class="empty-state">暂无路线点</li>` : routePlaces.map((place, index) => routeStop(place, index, routePlaces.length)).join("")}
      </ol>
    </section>

    <section class="tool-section place-section" aria-labelledby="places-title">
      <div class="section-title">
        <h2 id="places-title">地点</h2>
        ${selectedPlace ? `<button class="text-button" type="button" data-action="open-selected">打开</button>` : ""}
      </div>
      ${selectedPlace ? selectedSummary(selectedPlace) : ""}
      <div class="place-list">
        ${visiblePlaces.map(placeRow).join("")}
      </div>
    </section>
  `;

  bindSidebarEvents(sidebar);
}

function renderMarkers(): void {
  markerLayer.clearLayers();
  const places = getVisiblePlaces();

  places.forEach((place) => {
    const marker = L.marker([place.lat, place.lng], {
      icon: createPlaceIcon(place)
    });

    marker.bindPopup(popupHtml(place), { closeButton: false, maxWidth: 260 });
    if (state.showChineseLabels && map.getZoom() >= LABEL_MIN_ZOOM) {
      marker.bindTooltip(preferredChineseLabel(place), {
        permanent: true,
        direction: "right",
        offset: [18, 0],
        opacity: 1,
        className: `place-label place-label--${place.type}`
      });
    }
    marker.on("click", () => {
      state.selectedPlaceId = place.id;
      renderSidebar();
      marker.openPopup();
    });
    markerLayer.addLayer(marker);
  });

  if (!hasFitInitialBounds && places.length > 0) {
    hasFitInitialBounds = true;
    fitPlaces(places);
  }
}

function renderRoute(): void {
  routeLayer.clearLayers();
  const base = getBasePlace();
  const routePlaces = getRoutePlaces();
  const routePath = base ? [base, ...routePlaces] : routePlaces;

  if (routePath.length < 2) {
    return;
  }

  L.polyline(routePath.map((place) => [place.lat, place.lng] as LatLngExpression), {
    color: "#2563eb",
    weight: 4,
    opacity: 0.82,
    dashArray: "8 8"
  }).addTo(routeLayer);
}

function renderDraftMarker(): void {
  if (draftMarker) {
    draftMarker.remove();
    draftMarker = null;
  }

  if (!state.draftCoordinate) {
    return;
  }

  draftMarker = L.marker([state.draftCoordinate.lat, state.draftCoordinate.lng], {
    icon: L.divIcon({
      className: "",
      html: `<span class="draft-marker"></span>`,
      iconSize: [26, 26],
      iconAnchor: [13, 13]
    })
  }).addTo(map);
}

function bindSidebarEvents(sidebar: HTMLDivElement): void {
  sidebar.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const actionButton = target.closest<HTMLElement>("[data-action]");
    const cityButtonElement = target.closest<HTMLButtonElement>("[data-city]");
    const typeButtonElement = target.closest<HTMLButtonElement>("[data-type]");
    const rowElement = target.closest<HTMLElement>("[data-place-row]");

    if (cityButtonElement) {
      state.filters.city = cityButtonElement.dataset.city ?? "all";
      render();
      return;
    }

    if (typeButtonElement) {
      const type = typeButtonElement.dataset.type as PlaceType | undefined;
      if (type) {
        if (state.filters.types.has(type)) {
          state.filters.types.delete(type);
        } else {
          state.filters.types.add(type);
        }
        render();
      }
      return;
    }

    if (actionButton) {
      handleAction(actionButton);
      return;
    }

    if (rowElement) {
      const placeId = rowElement.dataset.placeRow;
      if (placeId) {
        selectAndPanToPlace(placeId);
      }
    }
  });

  sidebar.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    if (target.dataset.field === "priority") {
      state.filters.priority = target.value as AppState["filters"]["priority"];
      render();
    }
    if (target.dataset.field === "confidence") {
      state.filters.confidence = target.value as AppState["filters"]["confidence"];
      render();
    }
    if (target.dataset.field === "base") {
      state.baseId = target.value;
      localStorage.setItem(BASE_ID_KEY, state.baseId);
      render();
    }
    if (target.dataset.field === "show-labels") {
      state.showChineseLabels = (target as HTMLInputElement).checked;
      localStorage.setItem(LABELS_KEY, String(state.showChineseLabels));
      render();
    }
  });

  sidebar.addEventListener("input", (event) => {
    const target = event.target as HTMLInputElement;
    if (target.dataset.field === "query") {
      state.query = target.value;
      render();
    }
  });

  const form = sidebar.querySelector<HTMLFormElement>("#add-place-form");
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    addPlaceFromForm(form);
  });

  const mapTilerForm = sidebar.querySelector<HTMLFormElement>("#maptiler-key-form");
  mapTilerForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    void saveMapTilerKeyFromForm(mapTilerForm);
  });
}

function handleAction(element: HTMLElement): void {
  const action = element.dataset.action;
  const placeId = element.dataset.placeId;

  if (action === "reset-filters") {
    state.filters = {
      city: "all",
      types: new Set<PlaceType>(),
      priority: "all",
      confidence: "all"
    };
    state.query = "";
    render();
  }

  if (action === "collapse-sidebar") {
    setSidebarCollapsed(true);
  }

  if (action === "expand-sidebar") {
    setSidebarCollapsed(false);
  }

  if (action === "locate-place" && placeId) {
    selectAndPanToPlace(placeId);
  }

  if (action === "add-route" && placeId) {
    addRouteStop(placeId);
  }

  if (action === "remove-route" && placeId) {
    state.routeIds = state.routeIds.filter((id) => id !== placeId);
    persistRoute();
    render();
  }

  if (action === "move-route" && placeId) {
    moveRouteStop(placeId, Number(element.dataset.direction ?? 0));
  }

  if (action === "optimize-route") {
    optimizeRoute();
  }

  if (action === "clear-route") {
    state.routeIds = [];
    persistRoute();
    render();
  }

  if (action === "open-google") {
    openCurrentRouteInGoogle();
  }

  if (action === "export-json") {
    exportPlacesJson();
  }

  if (action === "open-selected" && state.selectedPlaceId) {
    openPlaceInGoogle(state.selectedPlaceId);
  }

  if (action === "open-place" && placeId) {
    openPlaceInGoogle(placeId);
  }

  if (action === "clear-maptiler-key") {
    void clearMapTilerKey();
  }
}

function setSidebarCollapsed(collapsed: boolean): void {
  state.sidebarCollapsed = collapsed;
  localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  render();
  window.setTimeout(() => {
    map.invalidateSize({ animate: true });
  }, 180);
}

function addPlaceFromForm(form: HTMLFormElement): void {
  const data = new FormData(form);
  const name = String(data.get("name") ?? "").trim();
  const city = String(data.get("city") ?? "").trim();
  const coordinates = parseCoordinateInput(String(data.get("coordinates") ?? ""));
  const type = String(data.get("type") ?? "temporary") as PlaceType;

  if (!name || !city || !coordinates) {
    form.classList.add("form-error");
    return;
  }

  const place = createTemporaryPlace({
    name,
    city,
    type,
    lat: coordinates.lat,
    lng: coordinates.lng,
    addressHint: String(data.get("addressHint") ?? ""),
    notes: String(data.get("notes") ?? "")
  });

  state.localPlaces = [...state.localPlaces, place];
  state.selectedPlaceId = place.id;
  state.draftCoordinate = null;
  saveLocalPlaces(state.localPlaces);
  form.reset();
  render();
  selectAndPanToPlace(place.id);
}

async function saveMapTilerKeyFromForm(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  const key = normalizeMapTilerKey(String(data.get("mapTilerKey") ?? ""));

  state.mapTilerKey = key;
  if (key) {
    localStorage.setItem(MAPTILER_KEY_STORAGE_KEY, key);
  } else {
    localStorage.removeItem(MAPTILER_KEY_STORAGE_KEY);
  }

  await installMapTilerChineseLayer(key);
  render();
}

async function clearMapTilerKey(): Promise<void> {
  state.mapTilerKey = BUNDLED_MAPTILER_KEY;
  localStorage.removeItem(MAPTILER_KEY_STORAGE_KEY);
  await installMapTilerChineseLayer(state.mapTilerKey);
  render();
}

function cityButton(value: string, label: string): string {
  const active = state.filters.city === value;
  return `
    <button class="segmented-item" type="button" data-city="${escapeHtml(value)}" aria-pressed="${active}">
      ${escapeHtml(label)}
    </button>
  `;
}

function typeToggle(type: PlaceType): string {
  const active = state.filters.types.size === 0 || state.filters.types.has(type);
  const pressed = state.filters.types.has(type);
  return `
    <button class="type-toggle type-toggle--${type}" type="button" data-type="${type}" aria-pressed="${pressed}">
      <span>${TYPE_META[type].glyph}</span>
      ${TYPE_META[type].label}
      <small>${active ? "显示" : "隐藏"}</small>
    </button>
  `;
}

function option(value: string, label: string, selectedValue: string): string {
  const selected = value === selectedValue ? "selected" : "";
  return `<option value="${escapeHtml(value)}" ${selected}>${escapeHtml(label)}</option>`;
}

function placeRow(place: Place): string {
  const selected = state.selectedPlaceId === place.id ? "is-selected" : "";
  const inRoute = state.routeIds.includes(place.id);

  return `
    <article class="place-row ${selected}" data-place-row="${escapeHtml(place.id)}">
      <div class="place-main">
        <span class="place-dot place-dot--${place.type}"></span>
        <div>
          <h3>${escapeHtml(place.name)}</h3>
          <p>${escapeHtml(place.addressHint)}</p>
          <div class="tag-row">
            <span>${escapeHtml(place.city)}</span>
            <span>${TYPE_META[place.type].label}</span>
            <span>${PRIORITY_LABELS[place.priority]}</span>
            <span>${CONFIDENCE_LABELS[place.confidence]}</span>
          </div>
        </div>
      </div>
      <div class="row-actions">
        <button class="icon-button" type="button" data-action="locate-place" data-place-id="${escapeHtml(place.id)}" title="定位" aria-label="定位 ${escapeHtml(place.name)}">
          <i data-lucide="map-pin"></i>
        </button>
        <button class="icon-button" type="button" data-action="add-route" data-place-id="${escapeHtml(place.id)}" title="加入路线" aria-label="加入路线 ${escapeHtml(place.name)}" ${inRoute ? "disabled" : ""}>
          <i data-lucide="plus"></i>
        </button>
        <button class="icon-button" type="button" data-action="open-place" data-place-id="${escapeHtml(place.id)}" title="Google Maps" aria-label="Google Maps ${escapeHtml(place.name)}">
          <i data-lucide="external-link"></i>
        </button>
      </div>
    </article>
  `;
}

function selectedSummary(place: Place): string {
  return `
    <div class="selected-summary selected-summary--${place.confidence}">
      <strong>${escapeHtml(place.name)}</strong>
      <span>${escapeHtml(`${place.lat}, ${place.lng}`)}</span>
    </div>
  `;
}

function routeStop(place: Place, index: number, count: number): string {
  return `
    <li class="route-stop">
      <span class="route-index">${index + 1}</span>
      <div>
        <strong>${escapeHtml(place.name)}</strong>
        <span>${escapeHtml(place.city)} · ${TYPE_META[place.type].label}</span>
      </div>
      <div class="row-actions">
        <button class="icon-button" type="button" data-action="move-route" data-place-id="${escapeHtml(place.id)}" data-direction="-1" ${index === 0 ? "disabled" : ""} title="上移" aria-label="上移">
          <i data-lucide="arrow-up"></i>
        </button>
        <button class="icon-button" type="button" data-action="move-route" data-place-id="${escapeHtml(place.id)}" data-direction="1" ${index === count - 1 ? "disabled" : ""} title="下移" aria-label="下移">
          <i data-lucide="arrow-down"></i>
        </button>
        <button class="icon-button" type="button" data-action="remove-route" data-place-id="${escapeHtml(place.id)}" title="移除" aria-label="移除">
          <i data-lucide="x"></i>
        </button>
      </div>
    </li>
  `;
}

function popupHtml(place: Place): string {
  return `
    <div class="map-popup">
      <strong>${escapeHtml(place.name)}</strong>
      <span>${escapeHtml(place.addressHint)}</span>
      <small>${escapeHtml(place.city)} · ${TYPE_META[place.type].label} · ${CONFIDENCE_LABELS[place.confidence]}</small>
    </div>
  `;
}

function preferredChineseLabel(place: Place): string {
  const parts = place.name.split(" / ").map((part) => part.trim());
  return parts.find((part) => /[\u3400-\u9fff]/.test(part)) ?? place.name;
}

function createPlaceIcon(place: Place): L.DivIcon {
  const selected = state.selectedPlaceId === place.id ? "marker--selected" : "";
  const route = state.routeIds.includes(place.id) ? "marker--route" : "";

  return L.divIcon({
    className: "",
    html: `
      <span class="place-marker marker--${place.type} marker--${place.confidence} ${selected} ${route}" title="${escapeHtml(place.name)}">
        ${TYPE_META[place.type].glyph}
      </span>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18]
  });
}

function getVisiblePlaces(): Place[] {
  const filtered = filterPlaces(allPlaces(), state.filters);
  const query = state.query.trim().toLowerCase();

  if (!query) {
    return filtered;
  }

  return filtered.filter((place) =>
    `${place.name} ${place.addressHint} ${place.notes} ${place.city}`
      .toLowerCase()
      .includes(query)
  );
}

function allPlaces(): Place[] {
  return mergePlaces(seedPlaces, state.localPlaces);
}

async function createMapTilerChineseLayer(key: string): Promise<L.Layer | null> {
  if (!hasMapTilerKey(key)) {
    return null;
  }

  await import("@maptiler/sdk/dist/maptiler-sdk.css");
  const { Language, MapStyle, MaptilerLayer } = await import("@maptiler/leaflet-maptilersdk");

  return new MaptilerLayer({
    apiKey: key,
    style: MapStyle.STREETS_V4,
    language: Language.SIMPLIFIED_CHINESE
  });
}

async function installMapTilerChineseLayer(key: string): Promise<void> {
  if (mapTilerChineseLayer) {
    if (map.hasLayer(mapTilerChineseLayer)) {
      map.removeLayer(mapTilerChineseLayer);
    }
    baseLayerControl.removeLayer(mapTilerChineseLayer);
    mapTilerChineseLayer = null;
  }

  const nextLayer = await createMapTilerChineseLayer(key);
  if (!nextLayer) {
    switchBaseLayer(cartoVoyagerLayer);
    return;
  }

  mapTilerChineseLayer = nextLayer;
  baseLayerControl.addBaseLayer(nextLayer, "中文矢量");
  switchBaseLayer(nextLayer);
}

function switchBaseLayer(layer: L.Layer): void {
  [...fallbackBaseLayers, ...(mapTilerChineseLayer ? [mapTilerChineseLayer] : [])].forEach((candidate) => {
    if (candidate !== layer && map.hasLayer(candidate)) {
      map.removeLayer(candidate);
    }
  });

  if (!map.hasLayer(layer)) {
    layer.addTo(map);
  }
}

function loadMapTilerKey(): string {
  return normalizeMapTilerKey(localStorage.getItem(MAPTILER_KEY_STORAGE_KEY)) || BUNDLED_MAPTILER_KEY;
}

function getRoutePlaces(): Place[] {
  const byId = new Map(allPlaces().map((place) => [place.id, place]));
  return state.routeIds.flatMap((id) => {
    const place = byId.get(id);
    return place ? [place] : [];
  });
}

function getBasePlace(): Place | null {
  return allPlaces().find((place) => place.id === state.baseId) ?? null;
}

function addRouteStop(placeId: string): void {
  if (!state.routeIds.includes(placeId)) {
    state.routeIds = [...state.routeIds, placeId];
    persistRoute();
  }
  state.selectedPlaceId = placeId;
  render();
}

function moveRouteStop(placeId: string, direction: number): void {
  const currentIndex = state.routeIds.indexOf(placeId);
  const nextIndex = currentIndex + direction;

  if (currentIndex < 0 || nextIndex < 0 || nextIndex >= state.routeIds.length) {
    return;
  }

  const nextRouteIds = [...state.routeIds];
  const [id] = nextRouteIds.splice(currentIndex, 1);
  if (id) {
    nextRouteIds.splice(nextIndex, 0, id);
    state.routeIds = nextRouteIds;
    persistRoute();
    render();
  }
}

function optimizeRoute(): void {
  const base = getBasePlace();
  const routePlaces = getRoutePlaces();

  if (!base || routePlaces.length < 2) {
    return;
  }

  state.routeIds = optimizeRouteOrder(base, routePlaces).map((place) => place.id);
  persistRoute();
  render();
}

function openCurrentRouteInGoogle(): void {
  const base = getBasePlace();
  const routePlaces = getRoutePlaces();
  const places = base ? [base, ...routePlaces] : routePlaces;

  if (places.length < 2) {
    return;
  }

  window.open(buildGoogleMapsDirectionsUrl(places), "_blank", "noopener,noreferrer");
}

function openPlaceInGoogle(placeId: string): void {
  const place = allPlaces().find((candidate) => candidate.id === placeId);
  if (!place) {
    return;
  }

  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${place.lat},${place.lng}`)}`,
    "_blank",
    "noopener,noreferrer"
  );
}

function selectAndPanToPlace(placeId: string): void {
  const place = allPlaces().find((candidate) => candidate.id === placeId);
  if (!place) {
    return;
  }

  state.selectedPlaceId = place.id;
  map.setView([place.lat, place.lng], Math.max(map.getZoom(), 15), {
    animate: true
  });
  render();
}

function fitPlaces(places: Place[]): void {
  const bounds = L.latLngBounds(places.map((place) => [place.lat, place.lng] as LatLngExpression));
  map.fitBounds(bounds.pad(0.16), { maxZoom: 13 });
}

function syncDraftCoordinateInput(): void {
  const input = document.querySelector<HTMLInputElement>('[data-field="draft-coordinate"]');
  if (input && state.draftCoordinate) {
    input.value = `${state.draftCoordinate.lat}, ${state.draftCoordinate.lng}`;
  }
}

function exportPlacesJson(): void {
  const payload = JSON.stringify(allPlaces(), null, 2);
  const blob = new Blob([payload], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "mapitup-places.json";
  link.click();
  URL.revokeObjectURL(url);
}

function loadLocalPlaces(): Place[] {
  const raw = localStorage.getItem(LOCAL_PLACES_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isPlace);
  } catch {
    return [];
  }
}

function saveLocalPlaces(places: Place[]): void {
  localStorage.setItem(LOCAL_PLACES_KEY, JSON.stringify(places));
}

function loadRouteIds(): string[] {
  const raw = localStorage.getItem(ROUTE_IDS_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
}

function persistRoute(): void {
  localStorage.setItem(ROUTE_IDS_KEY, JSON.stringify(state.routeIds));
}

function isPlace(value: unknown): value is Place {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<Place>;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.city === "string" &&
    typeof candidate.lat === "number" &&
    typeof candidate.lng === "number"
  );
}

function roundCoordinate(value: number): number {
  return Number(value.toFixed(6));
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
